import { Button, Select } from '@ohear/ui'
import { useRef, useState } from 'react'
import { type TaxFormType, useTaxFormDownloadUrl, useUploadTaxForm } from '../../lib/api/tax'

/**
 * Tax form upload + view widget.
 *
 * Renders a form-type select (W-9 / W-8BEN / W-8BEN-E), a file picker, and
 * a "View" link when a form is already on file. The actual upload is the
 * 3-step presign+PUT+finalize flow encapsulated in `useUploadTaxForm`.
 *
 * View URLs are short-lived (5 minutes per the API contract) so the hook
 * lazily fetches them on click rather than rendering the URL into the DOM
 * up front.
 */
export interface TaxFormUploadProps {
  formTypeOnFile: TaxFormType
  formUploadedAt: string | null
  formSha256: string | null
  /** Translated text for the labels. */
  copy: {
    chooseTypeLabel: string
    typeOptions: { value: 'w9' | 'w8ben' | 'w8ben_e'; label: string }[]
    uploadButton: string
    replaceButton: string
    viewButton: string
    pdfHint: string
    onFileSince: (when: string) => string
    sha256Label: string
    chooseFile: string
  }
}

export function TaxFormUpload({
  formTypeOnFile,
  formUploadedAt,
  formSha256,
  copy,
}: TaxFormUploadProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const upload = useUploadTaxForm()
  const [pendingType, setPendingType] = useState<'w9' | 'w8ben' | 'w8ben_e'>(
    formTypeOnFile === 'pending' ? 'w9' : formTypeOnFile,
  )
  const [error, setError] = useState<string | null>(null)
  const onFile = formTypeOnFile !== 'pending' && Boolean(formUploadedAt)

  // Download URL is only requested when the user clicks "View" — keeps the
  // 5-minute presigned URL fresh.
  const download = useTaxFormDownloadUrl(false)

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-[13px] max-w-xs">
        <span className="font-medium">{copy.chooseTypeLabel}</span>
        <Select
          value={pendingType}
          onChange={(e) => setPendingType(e.target.value as 'w9' | 'w8ben' | 'w8ben_e')}
          disabled={upload.isPending}
        >
          {copy.typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </label>

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (!file) return
          setError(null)
          try {
            await upload.mutateAsync({ formType: pendingType, file })
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed')
          } finally {
            // Reset the input so picking the same file twice still triggers change.
            if (fileInput.current) fileInput.current.value = ''
          }
        }}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant={onFile ? 'ghost' : 'primary'}
          loading={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {onFile ? copy.replaceButton : copy.uploadButton}
        </Button>

        {onFile ? (
          <Button
            variant="ghost"
            loading={download.isFetching}
            onClick={async () => {
              const fresh = await download.refetch()
              if (fresh.data?.download_url) {
                window.open(fresh.data.download_url, '_blank', 'noopener,noreferrer')
              }
            }}
          >
            {copy.viewButton}
          </Button>
        ) : null}
      </div>

      <p className="text-[12px] text-[var(--text-2)] m-0">{copy.pdfHint}</p>

      {onFile && formUploadedAt ? (
        <p className="text-[12px] text-[var(--text-3)] m-0">
          {copy.onFileSince(new Date(formUploadedAt).toLocaleDateString())}
          {formSha256 ? (
            <>
              {' · '}
              <span className="font-mono">
                {copy.sha256Label}: {formSha256.slice(0, 12)}…
              </span>
            </>
          ) : null}
        </p>
      ) : null}

      {error ? <p className="text-[12px] text-[var(--text-2)]">{error}</p> : null}
    </div>
  )
}
